import { FC, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { IconCaretDown } from 'src/general/Icons/IconCaretDown';
import { IconCaretUp } from 'src/general/Icons/IconCaretUp';
import { BadgeIllustration } from 'src/general/components/BadgeIllustration/BadgeIllustration';
import { BADGE } from 'src/general/components/BadgeIllustration/BadgeIllustration.type';
import { Button } from 'src/general/components/Button/Button';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { getMicrocopyBulk } from 'src/stores/microcopyStore';

import classNames from 'classnames';
import styles from './ServiceHistory.module.scss';

const HEADER_OFFSET = 130;

type ServiceHistoryRecord = {
  serviceDate?: string;
  kilometres?: number;
  recordFormat?: string;
  provider: string;
};

type Props = {
  serviceHistory?: ServiceHistoryRecord[];
  onDataEvent: () => void;
};

export const ServiceHistory: FC<Props> = ({ serviceHistory, onDataEvent }) => {
  const [isShowList, setIsShowList] = useState(false);
  const latestItem = serviceHistory?.[0] ?? null;
  const {
    defaultHeader,
    sectionHeader,
    oneServiceHeader,
    likeNewHeader,
    showButtonLabel,
    hideButtonLabel,
    noServiceDateMessage,
    noKilometreMessage,
  } = getMicrocopyBulk({
    defaultHeader: [MICROCOPY.PDP_SERVICE_HISTORY_HEADER],
    sectionHeader: [MICROCOPY.PDP_SERVICE_HISTORY_SECTION_HEADER],
    oneServiceHeader: [MICROCOPY.PDP_SERVICE_HISTORY_ONE_SERVICE_HEADER],
    likeNewHeader: [MICROCOPY.PDP_SERVICE_HISTORY_LIKE_NEW_HEADER],
    showButtonLabel: [MICROCOPY.PDP_SERVICE_HISTORY_BUTTON_SHOW_LABEL],
    hideButtonLabel: [MICROCOPY.PDP_SERVICE_HISTORY_BUTTON_HIDE_LABEL],
    noServiceDateMessage: [MICROCOPY.PDP_SERVICE_HISTORY_NO_SERVICE_DATE_MESSAGE],
    noKilometreMessage: [MICROCOPY.PDP_SERVICE_HISTORY_NO_KILOMETRE_MESSAGE],
  });
  const header =
    serviceHistory && serviceHistory.length > 0
      ? serviceHistory.length > 1
        ? defaultHeader
        : oneServiceHeader
      : likeNewHeader;

  const containerRef = useRef<null | HTMLHeadingElement>(null);

  useUpdateEffect(() => {
    if (!isShowList && containerRef.current) {
      const accordionPosition = containerRef.current?.getBoundingClientRect().top;
      window.scrollTo({
        top: accordionPosition + window.scrollY - HEADER_OFFSET,
        behavior: 'smooth',
      });
    }
  }, [isShowList]);

  return (
    <>
      <div className={styles.header}>
        <h3 className={classNames('c-fw-bold', 'c-fs-h5')} ref={containerRef}>
          {sectionHeader}
        </h3>
      </div>
      <section className={styles.root}>
        <div className={styles.content}>
          <div className={styles.lastServiceContainer}>
            <BadgeIllustration
              background="none"
              shape="square"
              icon={BADGE.CARMA_TOUCH}
              className={styles.badgeIllustration}
            />
            <div className={styles.headerContainer}>
              <p className={classNames('c-fw-normal', 'c-fs-body2')}>{header}</p>
              {latestItem && (
                <div className={classNames(styles.latestItem, 'c-fw-bold', 'c-fs-body2')}>
                  <p data-testid="ServiceHistory-latestServiceDate" className={styles.serviceDate}>
                    {latestItem?.serviceDate ?? noServiceDateMessage}
                  </p>
                  <p>
                    {latestItem?.kilometres
                      ? formatNumber(latestItem.kilometres, { append: 'km' })
                      : noKilometreMessage}
                  </p>
                </div>
              )}
            </div>
          </div>
          {serviceHistory && serviceHistory.length > 1 && (
            <>
              <div className={styles.buttonContainer}>
                <Button
                  data-testid="ServiceHistory-expand"
                  variant="tertiary"
                  endIcon={isShowList ? <IconCaretUp /> : <IconCaretDown />}
                  onClick={() => {
                    setIsShowList(!isShowList);
                    onDataEvent();
                  }}
                >
                  {isShowList ? hideButtonLabel : showButtonLabel}
                </Button>
              </div>
              {isShowList && (
                <div className={styles.listContainer}>
                  {serviceHistory.map((item) => (
                    <div className={styles.listItem} key={item.provider}>
                      <p className={classNames('c-fw-normal', 'c-fs-body2')}>
                        {item?.serviceDate ?? noServiceDateMessage}
                      </p>
                      <p className={classNames(styles.itemContent, 'c-fw-bold', 'c-fs-body2')}>
                        {item.kilometres ? formatNumber(item.kilometres, { append: 'km' }) : noKilometreMessage}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};
